import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ProviderProfileDto } from '@dto';
import { switchMap, take } from 'rxjs/operators';
import { PaginationInformation } from '../../shared/server-side-paginated-table/datasources/generic.datasource';
import { ProvidersIndexed } from '@utility';
import { PaginatedResponse } from '@models';

@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  public providers: Observable<ProviderProfileDto[]>;
  public _providerInactive = new BehaviorSubject<boolean>(false);

  public providerListByUserId = new BehaviorSubject<ProvidersIndexed>(null);
  public providerListByAuth0Id = new BehaviorSubject<ProvidersIndexed>(null);
  public providerListById = new BehaviorSubject<ProvidersIndexed>(null);

  private _providers = new BehaviorSubject<ProviderProfileDto[]>([]);

  constructor(private authService: AuthService, private httpClient: HttpClient) {
    this.providers = this._providers.asObservable();

    this.authService.authState
      .pipe(
        switchMap((authState) => {
          if (authState === null) {
            return of([]);
          }
          return this.getProviders();
        })
      )
      .subscribe(async (providers) => {
        this._providers.next(providers);
        this.populatedIndexedLists(providers);
        if (authService.currentUserAuth0Id) {
          const { deactivated } = await this.requestProviderById(authService.currentUserAuth0Id)
            .pipe(take(1))
            .toPromise();
          this._providerInactive.next(deactivated);
        }
      });
  }

  get currentProviderList(): ProviderProfileDto[] {
    return this._providers.getValue();
  }

  // Has to access a new v3 api endpoint
  public getProvidersPaginated(queryParams: HttpParams, pagination?: PaginationInformation) {
    if (pagination) {
      queryParams = queryParams.append('showInactive', pagination?.filters.showInactive as string);
    }
    const url = environment.apiV3Url.concat('/providers');
    return this.httpClient.get<PaginatedResponse<ProviderProfileDto>>(url, {
      params: queryParams,
    });
  }

  public getProviderByProviderId(providerId: number): ProviderProfileDto {
    const listProvider = this.currentProviderList.find((provider) => provider.id === providerId);
    if (listProvider) {
      return listProvider;
    }
    return null;
  }

  public getProviderByAuth0Id(auth0Id?: string): ProviderProfileDto {
    const listProvider = this.currentProviderList.find((provider) => provider.user.auth0Id === auth0Id);
    if (listProvider) {
      return listProvider;
    }
    return null;
  }

  public getProviderByAcuityCalendarId(acuityCalendarId: string): ProviderProfileDto {
    const targetProvider = this.currentProviderList.find((provider) => {
      return provider.acuityCalendarId === acuityCalendarId;
    });
    if (targetProvider) {
      return targetProvider;
    }
    return null;
  }

  public requestProviderById(userId: string) {
    const url = environment.apiV3Url.concat(`/providers/byUserId/${userId}`);
    return this.httpClient.get<ProviderProfileDto>(url);
  }

  getProviders() {
    const url = environment.apiUrl.concat('/provider-profiles');
    return this.httpClient.get<ProviderProfileDto[]>(url);
  }

  getProvider(providerId): Observable<ProviderProfileDto> {
    const url = environment.apiUrl.concat(`/provider-profiles/${providerId}`);
    return this.httpClient.get<ProviderProfileDto>(url);
  }

  private populatedIndexedLists(providers: ProviderProfileDto[]) {
    this.providerListByUserId.next(
      providers.reduce<ProvidersIndexed>((aggObject: ProvidersIndexed, profile) => {
        if ((profile as any).userId) {
          return Object.assign(aggObject, {
            [(profile as any).userId]: profile,
          });
        }
      }, {})
    );

    this.providerListByAuth0Id.next(
      providers.reduce<ProvidersIndexed>((aggObject: ProvidersIndexed, profile) => {
        return Object.assign(aggObject, {
          [profile.user.auth0Id]: profile,
        });
      }, {})
    );

    this.providerListById.next(
      providers.reduce<ProvidersIndexed>((aggObject: ProvidersIndexed, profile) => {
        return Object.assign(aggObject, {
          [profile.id.toString()]: profile,
        });
      }, {})
    );
  }
}
